import { BUILTIN_TOOL_NAMES } from '@klook/admin-ai-assistant'
import { getActPageTypeKey, PAGE_TYPE_KEY_DICT } from '@klook/admin-ai-workflow-contracts'
import { isMerchant } from '@/env'

// 工具名单一来源：workflow steps 与组件侧 v-tool-register 都引用本常量，避免裸字符串两侧 drift。
export const TOOL_NAMES = {
  SEARCH_ACT_LIST: 'wf:searchActListById',
  SEARCH_PACKAGE_LIST: 'wf:searchPackageListById',
  UPDATE_TIMESLOT_INV: 'wf:update_inventory:updateTimesoltInv',
  HIGHLIGHT_CONFIRM: 'wf:update_inventory:highlightConfirm',

  OPEN_AID_BULK_EDIT: 'wf:price_inv_drawer:openAidBulkEdit',
  OPEN_TTD_EDIT_SCHEDULE: 'wf:price_inv_drawer:openTtdEditSchedule',
  SET_DRAWER_SKU_IDS: 'wf:price_inv_drawer:setSkuIds',
  SET_DRAWER_DATE_RANGE: 'wf:price_inv_drawer:setDateRange',
  UPDATE_DRAWER_INVENTORY: 'wf:price_inv_drawer:updateInventory',
  UPDATE_DRAWER_PRICE: 'wf:price_inv_drawer:updatePrice',
  HIGHLIGHT_DRAWER_CONFIRM: 'wf:price_inv_drawer:highlightConfirm',
  WAIT_DRAWER_SAVE_RESULT: 'wf:price_inv_drawer:waitSaveResult',

  OPEN_TTD_FIXED_PRICE_SETTINGS: 'wf:update_price:openTtdFixedPriceSettings',
  SET_TTD_FIXED_COST_PRICE: 'wf:update_price:setTtdFixedCostPrice',
  SET_TTD_FIXED_SELLING_PRICE: 'wf:update_price:setTtdFixedSellingPrice',
  HIGHLIGHT_TTD_FIXED_PRICE_CONFIRM: 'wf:update_price:highlightTtdFixedPriceConfirm',
  OPEN_AID_FIXED_PRICE_DRAWER: 'wf:update_price:openAidFixedPriceDrawer',
  SET_AID_FIXED_COST_PRICE: 'wf:update_price:setAidFixedCostPrice',
  SET_AID_FIXED_SELLING_PRICE: 'wf:update_price:setAidFixedSellingPrice',
  HIGHLIGHT_AID_FIXED_PRICE_CONFIRM: 'wf:update_price:highlightAidFixedPriceConfirm',

  WAIT_PRICE_TASK_DONE: 'wf:update_price:waitPriceTaskDone',
  OPEN_SUBMIT_COST_DRAWER: 'wf:update_price:openSubmitCostDrawer',
  PREFILL_SUBMIT_COST_NOTE: 'wf:update_price:prefillSubmitCostNote',
  CLICK_SUBMIT_COST_CONFIRM: 'wf:update_price:clickSubmitCostConfirm',
  WAIT_SUBMIT_COST_RESULT: 'wf:update_price:waitSubmitCostResult',

  SET_TTD_TOTAL_INVENTORY: 'wf:update_inventory:setTtdTotalInventory',
  SET_AID_TOTAL_INVENTORY: 'wf:update_inventory:setAidTotalInventory',

  SET_CONFIRMATION_TIME: 'wf:toggle_instant_confirm:setConfirmationTime',

  SET_CANCELLATION_POLICY: 'wf:set_cancel_policy:setCancellationPolicy',

  OPEN_AID_ITEM_GROUP_MODAL: 'wf:booking_num:openAidItemGroupModal',
  SET_AID_BOOKING_MIN: 'wf:booking_num:setAidBookingMin',
  SET_AID_BOOKING_MAX: 'wf:booking_num:setAidBookingMax',
  HIGHLIGHT_AID_ITEM_GROUP_CONFIRM: 'wf:booking_num:highlightAidItemGroupConfirm',
  SET_TTD_MIN_MAX_BOOKINGS: 'wf:booking_num:setTtdMinMaxBookings',

  OPEN_AID_MANAGE_STATUS: 'wf:toggle_date:openAidManageStatus',
  APPLY_AID_DRAWER: 'wf:toggle_date:applyAidDrawer',
  HIGHLIGHT_AID_DRAWER_CONFIRM: 'wf:toggle_date:highlightAidDrawerConfirm',
  OPEN_BULK_SCHEDULE_MENU: 'wf:toggle_date:openBulkScheduleMenu',
  TOGGLE_DATE_APPLY: 'wf:toggle_date:applyDates',
  TOGGLE_DATE_HIGHLIGHT_CONFIRM: 'wf:toggle_date:highlightConfirm',

  HOVER_ACT_MORE_POPOVER: 'wf:submit_audit:hoverActMorePopover',
  HIGHLIGHT_SUBMIT_ACTIVITY: 'wf:submit_audit:highlightSubmitActivity',

  HIGHLIGHT_ACT_STATUS_BTN: 'wf:update_status:highlightActStatusBtn',
  HIGHLIGHT_PKG_STATUS_BTN: 'wf:update_status:highlightPkgStatusBtn',

  // 批量执行辅助工具（AiBatchHost 常驻注册）：信号预备/等待真实结果/不支持项拒绝
  ARM_OPERATION_SIGNAL: 'wf:batch:armOperationSignal',
  WAIT_OPERATION_RESULT: 'wf:batch:waitOperationResult',
  BATCH_UNSUPPORTED: 'wf:batch:unsupported'
}

const ACTION_TO_BULK_KEY = {
  close: 'deactivate',
  open: 'activate'
}

// 批量模式判定：BatchCoordinator 在下发 item.data 上注入 _batch_mode
const isBatchMode = (data) => !!(data && data._batch_mode)

// 批量代点通用尾部：预备信号 → 点确认弹窗按钮 → 等业务埋点的真实结果。
// confirmSelector 为二次确认弹窗的确认按钮；点击前先 WAIT_ELEMENT 等弹窗就绪，
// 避免 CLICK_ELEMENT 在弹窗动画期误判找不到元素。
function buildBatchConfirmSteps(operation, confirmSelector) {
  return [
    {
      name: TOOL_NAMES.ARM_OPERATION_SIGNAL,
      description: 'Prepare to capture the result',
      params: { operation },
      waitTimeout: 15000
    },
    {
      name: BUILTIN_TOOL_NAMES.WAIT_ELEMENT,
      description: 'Wait for the confirm dialog',
      params: { selector: confirmSelector, timeout: 10000 }
    },
    {
      name: BUILTIN_TOOL_NAMES.CLICK_ELEMENT,
      description: 'Confirm the operation',
      params: { selector: confirmSelector },
      delay: 300
    },
    {
      name: TOOL_NAMES.WAIT_OPERATION_RESULT,
      description: 'Wait for the operation result',
      params: { operation },
      timeout: 30000,
      waitTimeout: 15000
    }
  ]
}

function buildPriceInvDrawerSteps(kind) {
  const valueStepName = kind === 'price' ? TOOL_NAMES.UPDATE_DRAWER_PRICE : TOOL_NAMES.UPDATE_DRAWER_INVENTORY
  const valueStepDesc = kind === 'price' ? 'Update price' : 'Update inventory'
  return [
    {
      name: TOOL_NAMES.SET_DRAWER_SKU_IDS,
      description: 'Select SKU',
      waitTimeout: 15000
    },
    {
      name: TOOL_NAMES.SET_DRAWER_DATE_RANGE,
      description: 'Set date range',
      waitTimeout: 15000
    },
    {
      name: valueStepName,
      description: valueStepDesc,
      waitTimeout: 15000
    },
    {
      name: TOOL_NAMES.HIGHLIGHT_DRAWER_CONFIRM,
      description: 'Please click "Confirm" to save',
      waitTimeout: 15000
    },
    {
      // awaitAction 只等到"点击"；点击后的成败由本步骤等信号桥的真实结果。
      // 任何失败（表单校验未通过 / 取消确认弹窗 / 排期超限 / 接口报错）都在
      // 发生的瞬间定音失败并带真实原因——立即结束工作流、解除页面锁定，
      // 商户后续的补填提交属于手动接力，不再等待
      name: TOOL_NAMES.WAIT_DRAWER_SAVE_RESULT,
      description: 'Saving',
      waitTimeout: 15000,
      // 等待窗口只覆盖保存接口的真实延迟（失败路径均即时 record，不含用户操作时间）。
      // 上限受父侧失联 watchdog（DELEGATE_IDLE_TIMEOUT_MS 90s）约束，
      // 普通步骤静默超 90s 会被父侧判失联强杀，必须 < 90s
      timeout: 60000
    }
  ]
}

function buildPriceInvWorkflowSteps(data, kind) {
  const pageTypeKey = getActPageTypeKey(data)
  const isAID = pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT || pageTypeKey === PAGE_TYPE_KEY_DICT.AID_TOURS
  const openToolName = isAID ? TOOL_NAMES.OPEN_AID_BULK_EDIT : TOOL_NAMES.OPEN_TTD_EDIT_SCHEDULE

  return [
    {
      name: openToolName,
      description: 'Open price/inventory drawer',
      waitTimeout: 15000
    },
    ...buildPriceInvDrawerSteps(kind)
  ]
}

/**
 * Submit cost 追加步骤（V7.50#1 固定价改价自动提交审核；日历价复用同一链路）
 *
 * 触发条件：改价链路（固定价 / 日历价）+ 商户端 + 后端在 data 里下发 submit_cost 字段。
 * 后端未升级（无 submit_cost）时不追加，保持旧行为，前端可先行发版。
 *
 * 保存结果信号按 pricing_model 分流（OPEN_SUBMIT_COST_DRAWER 内判定）：
 *   固定价（pricing_model=0）→ fixedPriceSaveSignal；
 *   日历价 → priceInvSaveSignal（前置 WAIT_DRAWER_SAVE_RESULT 已定音成功，wait 立即返回）。
 *
 * data.submit_cost 契约：{ note_type?: 1|2|3|4, note_text?: string }
 *   note_type 对齐 submitApproveDrawer 的 noteDict（4=Other 时 note_text 必填）；
 *   缺省/非法时降级为只打开弹窗不预填原因，由用户手选。
 */
function buildSubmitCostSteps(data, kind) {
  if (kind !== 'price' || !isMerchant || !data.submit_cost) return []

  const steps = []

  // 日历价：batch_update_calendar_setting_v2 返回只代表任务受理（res.result.ref），
  // 价格落库与审核工单生成都在异步批量任务 done 之后（页面进度通知同源），
  // 必须先等任务终态再开 Submit Cost 弹窗，否则拉不到工单
  if (data.pricing_model !== 0) {
    steps.push({
      name: TOOL_NAMES.WAIT_PRICE_TASK_DONE,
      description: 'Waiting for the price update to complete',
      waitTimeout: 15000,
      // 工具内轮询 ~70s < step timeout 80s < 父侧失联 watchdog 90s
      timeout: 80000
    })
  }

  steps.push(
    {
      name: TOOL_NAMES.OPEN_SUBMIT_COST_DRAWER,
      description: 'Open the Submit Cost dialog',
      waitTimeout: 15000
    },
    {
      name: TOOL_NAMES.PREFILL_SUBMIT_COST_NOTE,
      description: 'Fill in the price change reason',
      waitTimeout: 15000,
      // 日历价审核工单为批量接口异步生成，工具内轮询重拉清单（约 30s），
      // 默认 30s step timeout 装不下，放宽到 60s（须 < 父侧失联 watchdog 90s）
      timeout: 60000
    },
    {
      name: TOOL_NAMES.CLICK_SUBMIT_COST_CONFIRM,
      description: 'Confirm and submit the cost for review',
      waitTimeout: 15000
    },
    {
      name: TOOL_NAMES.WAIT_SUBMIT_COST_RESULT,
      description: 'Waiting for the submission result',
      waitTimeout: 15000,
      // 用户点 Confirm 后校验未过可补填重试，等待窗口放宽到 60s
      timeout: 60000
    }
  )

  return steps
}

function buildFixedDrawerWorkflowSteps(data, kind) {
  const pageTypeKey = getActPageTypeKey(data)
  const isAID = pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT || pageTypeKey === PAGE_TYPE_KEY_DICT.AID_TOURS
  const isPrice = kind === 'price'
  // 售价场景：后端下发的 selling_price 与 cost price 同层级，非空即表示本次改售价而非成本价
  const isSelling = isPrice && data.selling_price != null
  const priceDesc = isSelling ? 'Fill in selling price' : 'Fill in cost price'

  if (isAID) {
    const aidPriceTool = isSelling
      ? TOOL_NAMES.SET_AID_FIXED_SELLING_PRICE
      : TOOL_NAMES.SET_AID_FIXED_COST_PRICE
    return [
      {
        name: BUILTIN_TOOL_NAMES.CLICK_ELEMENT,
        description: 'Select target SKU',
        params: {
          selector: (intent, d) => `[data-sku-id="${d.sku_id}"]`
        }
      },
      {
        name: TOOL_NAMES.OPEN_AID_FIXED_PRICE_DRAWER,
        description: 'Open fixed price / total inventory drawer',
        waitTimeout: 15000
      },
      {
        name: isPrice ? aidPriceTool : TOOL_NAMES.SET_AID_TOTAL_INVENTORY,
        description: isPrice ? priceDesc : 'Fill in total inventory',
        waitTimeout: 15000
      },
      {
        name: TOOL_NAMES.HIGHLIGHT_AID_FIXED_PRICE_CONFIRM,
        description: 'Please click "Save" to confirm',
        waitTimeout: 15000
      },
      ...buildSubmitCostSteps(data, kind)
    ]
  }

  const ttdPriceTool = isSelling
    ? TOOL_NAMES.SET_TTD_FIXED_SELLING_PRICE
    : TOOL_NAMES.SET_TTD_FIXED_COST_PRICE
  return [
    {
      name: TOOL_NAMES.OPEN_TTD_FIXED_PRICE_SETTINGS,
      description: 'Open "Edit price and inventory rules" drawer',
      waitTimeout: 15000
    },
    {
      name: isPrice ? ttdPriceTool : TOOL_NAMES.SET_TTD_TOTAL_INVENTORY,
      description: isPrice ? priceDesc : 'Fill in total inventory',
      waitTimeout: 15000
    },
    {
      name: TOOL_NAMES.HIGHLIGHT_TTD_FIXED_PRICE_CONFIRM,
      description: 'Please click "Save" to confirm',
      waitTimeout: 15000
    },
    ...buildSubmitCostSteps(data, kind)
  ]
}

const updateInventoryWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Update activity inventory',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id) return []
    if (data.inv_model === 3) {
      return buildFixedDrawerWorkflowSteps(data, 'inventory')
    }
    return buildPriceInvWorkflowSteps(data, 'inventory')
  }
}

const updatePriceWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Update activity price',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id) return []
    if (data.pricing_model === 0) {
      return buildFixedDrawerWorkflowSteps(data, 'price')
    }

    // 日历价：drawerV2 保存定音成功（WAIT_DRAWER_SAVE_RESULT）后，
    // 与固定价共用 submit cost 自动提交审核尾部
    return [...buildPriceInvWorkflowSteps(data, 'price'), ...buildSubmitCostSteps(data, 'price')]
  }
}

const toggleDateWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Bulk enable/disable date schedules',
  steps(call) {
    const data = call.data || {}
    const bulkKey = ACTION_TO_BULK_KEY[data.action]
    if (!data.activity_id || !Array.isArray(data.dates) || !data.dates.length || !bulkKey) {
      return []
    }

    // 批量一期只支持关闭销售（close）：批量开放有超卖风险、无产品诉求背书，
    // 后端意图层拦截 open，此处为漏网兜底（就地失败、不误执行）
    if (isBatchMode(data) && data.action !== 'close') {
      return [
        {
          name: TOOL_NAMES.BATCH_UNSUPPORTED,
          description: 'Unsupported batch operation',
          params: { reason: 'Batch open-for-sale is not supported in this phase' },
          waitTimeout: 15000
        }
      ]
    }

    const pageTypeKey = getActPageTypeKey(data)
    const isAID =
      pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT || pageTypeKey === PAGE_TYPE_KEY_DICT.AID_TOURS

    if (isAID) {
      const steps = [
        {
          name: BUILTIN_TOOL_NAMES.CLICK_ELEMENT,
          description: 'Select target SKU',
          // 不要传 target: 'iframe'：当前 executor 已运行在 iframe 上下文，
          // 再次 target:'iframe' 会让 CLICK_ELEMENT 找子 iframe，executeInIframe 无实现导致永久挂起。
          params: {
            selector: (intent, d) => `[data-sku-id="${d.sku_id}"]`
          }
        },
        {
          name: TOOL_NAMES.OPEN_AID_MANAGE_STATUS,
          description: 'Open Manage Status drawer',
          waitTimeout: 15000
        },
        {
          name: TOOL_NAMES.APPLY_AID_DRAWER,
          description: 'Fill in dates and action type',
          params: { bulkKey },
          waitTimeout: 15000
        }
      ]

      if (isBatchMode(data)) {
        // 批量模式：代点抽屉 Confirm（复用单卡 awaitAction 的同款选择器），等接口真实结果
        steps.push(
          ...buildBatchConfirmSteps(
            'toggle_date',
            '.merchant-manage-status-drawer-wrapper .footer .ant-btn:last-child'
          )
        )

        return steps
      }

      steps.push({
        name: TOOL_NAMES.HIGHLIGHT_AID_DRAWER_CONFIRM,
        description: 'Please click Confirm to submit',
        waitTimeout: 15000
      })

      return steps
    }

    const steps = [
      {
        name: TOOL_NAMES.OPEN_BULK_SCHEDULE_MENU,
        description: `Select bulk ${data.action === 'close' ? 'disable' : 'enable'} menu`,
        params: { bulkKey },
        waitTimeout: 15000
      },
      {
        name: TOOL_NAMES.TOGGLE_DATE_APPLY,
        description: 'Fill dates into the form',
        waitTimeout: 15000
      }
    ]

    if (isBatchMode(data)) {
      // 批量模式：代点表单提交按钮，等接口真实结果
      steps.push(
        ...buildBatchConfirmSteps(
          'toggle_date',
          '.bulk-manage-schedule-form-container .actions .ant-btn:last-child'
        )
      )

      return steps
    }

    steps.push({
      name: TOOL_NAMES.TOGGLE_DATE_HIGHLIGHT_CONFIRM,
      description: 'Please click the button to submit',
      waitTimeout: 15000
    })

    return steps
  }
}

const toggleInstantConfirmWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Toggle instant confirmation',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id || data.confirmation_time == null) return []

    const pageTypeKey = getActPageTypeKey(data)
    const isAID =
      pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT || pageTypeKey === PAGE_TYPE_KEY_DICT.AID_TOURS
    const saveSelector = isAID ? '.save_and_next_step' : '.js-edit-save-btn'

    return [
      {
        name: TOOL_NAMES.SET_CONFIRMATION_TIME,
        description: 'Set confirmation method',
        waitTimeout: 15000
      },
      {
        name: BUILTIN_TOOL_NAMES.HIGHLIGHT_ELEMENT,
        description: 'Please click the save button',
        params: { selector: saveSelector, label: 'Please click to save' },
        delay: 1000
      }
    ]
  }
}

const setCancelPolicyWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Open the cancellation policy page and set the policy',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id || data.cancellation_type == null) return []

    const pageTypeKey = getActPageTypeKey(data)
    const isAID =
      pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT || pageTypeKey === PAGE_TYPE_KEY_DICT.AID_TOURS
    const saveSelector = isAID ? '.save_and_next_step' : '.js-edit-save-btn'

    return [
      {
        name: TOOL_NAMES.SET_CANCELLATION_POLICY,
        description: 'Select cancellation policy option',
        waitTimeout: 15000
      },
      {
        name: BUILTIN_TOOL_NAMES.HIGHLIGHT_ELEMENT,
        description: 'Please click the save button',
        params: { selector: saveSelector, label: 'Please click to save' },
        delay: 1000
      }
    ]
  }
}

function buildUpdateBookingNumSteps(data) {
  const pageTypeKey = getActPageTypeKey(data)

  if (pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT) return []

  if (pageTypeKey === PAGE_TYPE_KEY_DICT.AID_TOURS) {
    const hasMin = data.min_num != null
    const hasMax = data.max_num != null

    const steps = [
      {
        name: TOOL_NAMES.OPEN_AID_ITEM_GROUP_MODAL,
        description: 'Open booking limit modal',
        waitTimeout: 15000
      }
    ]
    if (hasMin) {
      steps.push({
        name: TOOL_NAMES.SET_AID_BOOKING_MIN,
        description: 'Set minimum booking number',
        waitTimeout: 15000
      })
    }
    if (hasMax) {
      steps.push({
        name: TOOL_NAMES.SET_AID_BOOKING_MAX,
        description: 'Set maximum booking number',
        waitTimeout: 15000
      })
    }
    steps.push({
      name: TOOL_NAMES.HIGHLIGHT_AID_ITEM_GROUP_CONFIRM,
      description: 'Please click "Confirm" to save',
      waitTimeout: 15000
    })
    return steps
  }

  return [
    {
      name: TOOL_NAMES.SET_TTD_MIN_MAX_BOOKINGS,
      description: 'Set min / max booking number',
      waitTimeout: 15000
    },
    {
      name: BUILTIN_TOOL_NAMES.HIGHLIGHT_ELEMENT,
      description: 'Please click the save button',
      params: { selector: '.js-edit-save-btn', label: 'Please click to save' },
      delay: 1000
    }
  ]
}

const updateMinMaxNumWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Update min / max booking number',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id) return []
    if (data.min_num == null && data.max_num == null) return []
    return buildUpdateBookingNumSteps(data)
  }
}

const submitAuditWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Submit for review',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id) return []

    const isPackage = data.audit_type === 'package'
    // 套餐场景必须带 package_id，缺失则跳过以免下游定位失败
    if (isPackage && !data.package_id) return []

    const entityLabel = isPackage ? 'package' : 'activity'

    const steps = [
      {
        name: isPackage ? TOOL_NAMES.SEARCH_PACKAGE_LIST : TOOL_NAMES.SEARCH_ACT_LIST,
        description: `Switch to ${entityLabel} list and search`,
        waitTimeout: 15000
      },
      {
        name: TOOL_NAMES.HOVER_ACT_MORE_POPOVER,
        description: 'Expand the action menu',
        waitTimeout: 15000
      }
    ]

    if (isBatchMode(data)) {
      // 批量模式：代点菜单里的 Submit for review + 送审弹窗 Confirm，等接口真实结果
      const actionType = isPackage ? 'submitPackage' : 'submitActivity'
      steps.push(
        {
          name: BUILTIN_TOOL_NAMES.CLICK_ELEMENT,
          description: `Click "Submit for review" of the ${entityLabel}`,
          params: { selector: `[data-action-type="${actionType}"]` },
          delay: 300
        },
        ...buildBatchConfirmSteps('submit_audit', '[data-ai-role="stk-modal-ok"]')
      )

      return steps
    }

    steps.push({
      name: TOOL_NAMES.HIGHLIGHT_SUBMIT_ACTIVITY,
      description: `Please click the "Submit for review" button of the ${entityLabel}`,
      waitTimeout: 15000
    })

    return steps
  }
}

const updateStatusWorkflow = {
  trigger: 'auto',
  readyMode: 'lazy',
  scope: 'COMMON',
  description: 'Update activity status',
  steps(call) {
    const data = call.data || {}
    if (!data.activity_id) return []
    if (data.action !== 'publish' && data.action !== 'unpublish') return []

    const pageTypeKey = getActPageTypeKey(data)
    if (pageTypeKey === PAGE_TYPE_KEY_DICT.AID_ATT_ACT) return []

    const isPackageTarget = data.target === 'package'
    const isTTDPkg = pageTypeKey === PAGE_TYPE_KEY_DICT.TTD_ACTIVITY && isPackageTarget

    if (isTTDPkg && !data.package_id) {
      return []
    }

    // 批量一期只支持发布方向：下架必填下架原因（弹窗内下拉），无法代填，
    // 后端拦截漏网时就地失败并带明确原因（不误执行、不假成功）
    if (isBatchMode(data) && data.action !== 'publish') {
      return [
        {
          name: TOOL_NAMES.BATCH_UNSUPPORTED,
          description: 'Unsupported batch operation',
          params: { reason: 'Batch unpublish is not supported (unpublish reason is required)' },
          waitTimeout: 15000
        }
      ]
    }

    const steps = []
    if (isTTDPkg) {
      const lang = data.submitted_language || 'en_US'
      const route =
        `/act/package/info/${data.activity_id}` +
        `?lang=${lang}&package_type=-1&package_id=${data.package_id}`
      steps.push({
        name: BUILTIN_TOOL_NAMES.REDIRECT_ROUTER,
        description: 'Navigate to package info page',
        params: { route },
        waitTimeout: 15000
      })
    }

    if (isBatchMode(data)) {
      // 批量模式：代点发布按钮 + 二次确认弹窗 OK，等接口真实结果
      const actionType = isTTDPkg || isPackageTarget ? 'publish_package' : 'publish_activity'
      steps.push(
        {
          name: BUILTIN_TOOL_NAMES.CLICK_ELEMENT,
          description: 'Click the publish button',
          params: { selector: `[data-action-type="${actionType}"]` },
          delay: 300
        },
        ...buildBatchConfirmSteps('update_status', '[data-ai-role="update-status-confirm-ok"]')
      )

      return steps
    }

    steps.push({
      name: isTTDPkg ? TOOL_NAMES.HIGHLIGHT_PKG_STATUS_BTN : TOOL_NAMES.HIGHLIGHT_ACT_STATUS_BTN,
      description:
        data.action === 'publish' ? 'Please click the publish button' : 'Please click the unpublish button',
      waitTimeout: 15000
    })

    return steps
  }
}

export function registerWorkflows(ai) {
  ai.registerWorkflow('update_inventory', updateInventoryWorkflow)
  ai.registerWorkflow('update_price', updatePriceWorkflow)
  ai.registerWorkflow('toggle_date', toggleDateWorkflow)
  ai.registerWorkflow('toggle_instant_confirm', toggleInstantConfirmWorkflow)
  ai.registerWorkflow('set_cancel_policy', setCancelPolicyWorkflow)
  ai.registerWorkflow('update_min_max_num', updateMinMaxNumWorkflow)
  ai.registerWorkflow('submit_audit', submitAuditWorkflow)
  ai.registerWorkflow('update_status', updateStatusWorkflow)
}
